import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Box,
} from '@mui/material';

const ATTRIBUTES = ['land_cover', 'crop', 'primary_practice', 'secondary_practice', 'sourcing_year'];

interface HistoryFilterPopupProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: Record<string, string[]>) => void;
  initialFilters: Record<string, string[]>;
  attributeValues: Record<string, string[]>;
  isLoading?: boolean;
  onFetchValues?: () => void;
}

export const HistoryFilterPopup: React.FC<HistoryFilterPopupProps> = ({
  open,
  onClose,
  onApply,
  initialFilters,
  attributeValues,
  isLoading = false,
  onFetchValues,
}) => {
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>(initialFilters);

  useEffect(() => {
    if (open) {
      // Check if values are missing or empty, and fetch if needed
      const hasEmptyValues = ATTRIBUTES.some(attr =>
        attributeValues[attr] === undefined
      );
      if (hasEmptyValues && onFetchValues) {
        onFetchValues();
      }
    }
  }, [open, attributeValues, onFetchValues]);

  useEffect(() => {
    setSelectedFilters(initialFilters);
  }, [initialFilters]);

  const handleCheckboxChange = (attribute: string, value: string) => {
    setSelectedFilters((prev) => {
      const newValues = prev[attribute] ? [...prev[attribute]] : [];
      if (newValues.includes(value)) {
        return { ...prev, [attribute]: newValues.filter((v) => v !== value) };
      } else {
        return { ...prev, [attribute]: [...newValues, value] };
      }
    });
  };

  const handleApply = () => {
    onApply(selectedFilters);
    onClose();
  };

  const handleClear = () => {
    setSelectedFilters({});
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ backgroundColor: 'grey.100', borderBottom: 1, borderColor: 'divider' }}>
        Additional Filters
      </DialogTitle>
      <DialogContent sx={{ minHeight: '300px', p: 3, bgcolor: 'grey.50' }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%" sx={{pt: 2}}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ height: '100%', overflowY: 'auto', flexWrap: 'nowrap' }}>
            {ATTRIBUTES.map((attribute) => (
              <Grid size={3} key={attribute} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{pt: 2}}>
                  {attribute.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Typography>
                <Box sx={{ overflowY: 'auto', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  {(attributeValues[attribute] || []).map((value) => (
                    <FormControlLabel
                      key={value}
                      control={
                        <Checkbox
                          checked={(selectedFilters[attribute] || []).includes(value)}
                          onChange={() => handleCheckboxChange(attribute, value)}
                        />
                      }
                      label={value}
                    />
                  ))}
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ backgroundColor: 'grey.100', borderTop: 1, borderColor: 'divider', p: '12px 24px' }}>
        <Button onClick={handleClear} color="error">
          Clear
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleApply} variant="contained">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};
